import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MAX_ALBUM, type MediaItem } from "@tg/shared";
import { api } from "../lib/api";
import { useDraft } from "../lib/hooks";
import { countVariants, expandSpintax, lintSpintax } from "../tg/spintax";
import { Banner, Button, Card, cn } from "../ui";

export function Compose() {
  const qc = useQueryClient();
  const draftQ = useDraft();

  const [caption, setCaption] = useState("");
  const [varyCaption, setVaryCaption] = useState(true);
  const [varyFilename, setVaryFilename] = useState(false);
  const [varyBytes, setVaryBytes] = useState(false);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const loaded = useRef(false);

  useEffect(() => {
    if (draftQ.data && !loaded.current) {
      loaded.current = true;
      setCaption(draftQ.data.captionTemplate);
      setVaryCaption(draftQ.data.varyCaption);
      setVaryFilename(draftQ.data.varyFilename);
      setVaryBytes(draftQ.data.varyBytes);
      setMedia(draftQ.data.media);
      // fetch thumbnails for already-saved media
      draftQ.data.media.forEach((m) => {
        api
          .getImageBlob(m.key)
          .then((b) => setPreviews((p) => ({ ...p, [m.key]: URL.createObjectURL(b) })))
          .catch(() => {});
      });
    }
  }, [draftQ.data]);

  const lint = useMemo(() => lintSpintax(caption), [caption]);
  const variants = useMemo(() => countVariants(caption), [caption]);
  useEffect(() => setPreview(expandSpintax(caption)), [caption]);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function scheduleSave(patch: Parameters<typeof api.putDraft>[0]) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.putDraft(patch).then(() => qc.invalidateQueries({ queryKey: ["draft"] }));
    }, 500);
  }
  function saveMedia(next: MediaItem[]) {
    setMedia(next);
    api.putDraft({ media: next }).then(() => qc.invalidateQueries({ queryKey: ["draft"] }));
  }

  async function addFiles(files: FileList) {
    setUploading(true);
    try {
      const slots = MAX_ALBUM - media.length;
      const chosen = Array.from(files).slice(0, Math.max(0, slots));
      const added: MediaItem[] = [];
      for (const file of chosen) {
        const norm = await normalizeForPhoto(file);
        const buf = await norm.blob.arrayBuffer();
        const res = await api.uploadImage(buf, norm.mime, norm.w, norm.h);
        const item: MediaItem = { key: res.imageKey, w: res.w, h: res.h, mime: res.mime, size: res.size };
        setPreviews((p) => ({ ...p, [item.key]: URL.createObjectURL(norm.blob) }));
        added.push(item);
      }
      if (added.length) saveMedia([...media, ...added]);
    } finally {
      setUploading(false);
    }
  }
  function removeAt(i: number) {
    saveMedia(media.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= media.length) return;
    const next = media.slice();
    [next[i], next[j]] = [next[j]!, next[i]!];
    saveMedia(next);
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Redactar</h1>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Card className="space-y-2">
            <h2 className="text-sm font-medium text-slate-300">Texto (spintax)</h2>
            <p className="text-xs text-slate-500">
              Usa <code>{"{Hola|Buenas}"}</code> para variar el texto en cada envío y reducir el riesgo de spam.
            </p>
            <textarea
              className="h-40 w-full rounded-md border border-slate-700 bg-slate-900 p-3 text-sm text-slate-100 outline-none focus:border-sky-500"
              value={caption}
              onChange={(e) => {
                setCaption(e.target.value);
                scheduleSave({ captionTemplate: e.target.value });
              }}
              placeholder="{Hola|Buenas} a todos 👋 ..."
            />
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">
                {variants.toLocaleString()} variantes · {caption.length}/1024
              </span>
              {caption.length > 1024 && <span className="text-amber-400">Supera 1024 caracteres</span>}
            </div>
            {lint.errors.map((e, i) => (
              <Banner key={i} kind="danger">{e}</Banner>
            ))}
            {lint.warnings.map((w, i) => (
              <Banner key={i} kind="warn">{w}</Banner>
            ))}
          </Card>

          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-slate-300">Imágenes (álbum)</h2>
              <span className="text-xs text-slate-500">{media.length}/{MAX_ALBUM}</span>
            </div>
            <p className="text-xs text-slate-500">
              Hasta {MAX_ALBUM} imágenes en un solo mensaje. Se envían como FOTOS (no como archivos adjuntos).
            </p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {media.map((m, i) => (
                <div key={m.key} className="group relative aspect-square overflow-hidden rounded border border-slate-700">
                  {previews[m.key] ? (
                    <img src={previews[m.key]} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-slate-500">…</div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 px-1 py-0.5 text-xs">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="disabled:opacity-30">◀</button>
                    <button onClick={() => removeAt(i)} className="text-red-300">✕</button>
                    <button onClick={() => move(i, 1)} disabled={i === media.length - 1} className="disabled:opacity-30">▶</button>
                  </div>
                </div>
              ))}
              {media.length < MAX_ALBUM && (
                <label className="flex aspect-square cursor-pointer items-center justify-center rounded border border-dashed border-slate-600 text-2xl text-slate-500 hover:border-sky-500 hover:text-sky-400">
                  +
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && addFiles(e.target.files)}
                  />
                </label>
              )}
            </div>
            {uploading && <p className="text-xs text-slate-500">Subiendo…</p>}
          </Card>

          <Card className="space-y-2">
            <h2 className="text-sm font-medium text-slate-300">Variación por envío</h2>
            {[
              { k: "varyCaption", v: varyCaption, set: setVaryCaption, label: "Variar texto (spintax) — recomendado" },
              { k: "varyFilename", v: varyFilename, set: setVaryFilename, label: "Variar nombre de archivo" },
              { k: "varyBytes", v: varyBytes, set: setVaryBytes, label: "Variar bytes de la imagen" },
            ].map((o) => (
              <label key={o.k} className="flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={o.v}
                  onChange={(e) => {
                    o.set(e.target.checked);
                    scheduleSave({ [o.k]: e.target.checked } as Parameters<typeof api.putDraft>[0]);
                  }}
                />
                {o.label}
              </label>
            ))}
          </Card>
        </div>

        <Card className="space-y-3 self-start">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-slate-300">Vista previa</h2>
            <Button variant="ghost" onClick={() => setPreview(expandSpintax(caption))}>🎲 Otra variante</Button>
          </div>
          <div className="rounded-lg bg-slate-800 p-3">
            {media.length > 0 ? (
              <div className={cn("mb-2 grid gap-1", media.length === 1 ? "grid-cols-1" : "grid-cols-2")}>
                {media.slice(0, 4).map((m, i) => (
                  <img
                    key={m.key}
                    src={previews[m.key]}
                    alt=""
                    className={cn("w-full rounded object-cover", media.length === 1 ? "max-h-64" : "h-28")}
                  />
                ))}
              </div>
            ) : (
              <div className="mb-2 flex h-40 items-center justify-center rounded bg-slate-700 text-xs text-slate-400">
                sin imágenes
              </div>
            )}
            <p className={cn("whitespace-pre-wrap text-sm", preview ? "text-slate-100" : "text-slate-500")}>
              {preview || "(texto vacío)"}
            </p>
          </div>
          <p className="text-xs text-slate-500">
            {media.length ? `${media.length} imagen(es) guardada(s) ✓` : "Sube al menos una imagen para enviar."}
          </p>
        </Card>
      </div>
    </div>
  );
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Guarantee Telegram renders the media as a PHOTO: JPEG/PNG pass through; exotic
// formats (AVIF, WEBP, GIF, HEIC…) are re-encoded to JPEG via canvas.
async function normalizeForPhoto(
  file: File,
): Promise<{ blob: Blob; mime: string; w: number; h: number }> {
  const img = await loadImage(file);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const t = (file.type || "").toLowerCase();
  if (t === "image/jpeg" || t === "image/png") return { blob: file, mime: t, w, h };
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return { blob: file, mime: t || "image/jpeg", w, h };
  ctx.drawImage(img, 0, 0);
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/jpeg", 0.9),
  );
  return { blob, mime: "image/jpeg", w, h };
}
